import { useState } from 'react'

// Exported so pages/products/secure-flow can emit FAQPage schema from the same copy.
export const FAQS = [
  {
    q: 'How long does deployment take?',
    a: "Timelines depend on integration scope: number of payment rails, existing case-management systems, and whether you deploy on cloud or on-prem. Pilot deployments are typically faster than full production rollouts. We'll give you a concrete timeline after the scoping call once we understand your environment.",
  },
  {
    q: 'What support model is available?',
    a: 'Pilot tier ships with email support. Production tier includes incident response and a named customer-success engineer. Enterprise plans add a dedicated solutions architect and quarterly rule-tuning reviews. Exact SLA tiers are confirmed in the commercial agreement based on your operating posture.',
  },
  {
    q: 'Which integrations and payment rails are supported?',
    a: "Native coverage for UPI, IMPS, NEFT, RTGS, SWIFT and ISO 20022 ready (pacs.008 and pacs.002). REST APIs are available for everything else. We integrate with major core-banking systems, payment switches and case-management platforms. We'll build any connector you need during onboarding.",
  },
  {
    q: 'What about compliance and certifications?',
    a: "The platform is designed around RBI compliance posture, PMLA reporting and ISO 20022 ready. India-resident data is supported. Specific security certifications and vendor-risk packs come up during procurement. We'll share what we hold and what is on the roadmap when you reach out.",
  },
  {
    q: 'How is pricing structured?',
    a: "Production pricing is volume-based, by transactions evaluated per month, not per seat or per rule. Pilots are available, so get in touch and we'll discuss what makes sense for your environment. We provide a written commercial proposal after the scoping call.",
  },
  {
    q: 'Can compliance teams build rules without engineering support?',
    a: 'Yes. The visual rule builder lets compliance and risk teams design, test against historical data, and deploy new rules without filing engineering tickets. Engineers retain full code-level control for advanced typologies and custom integrations.',
  },
]

export default function FAQSection() {
  const [openIdx, setOpenIdx] = useState(0)
  const toggle = (i) => setOpenIdx(openIdx === i ? -1 : i)

  return (
    <section className="sf-faq" id="faq">
      <div className="sf-container">
        <div className="sf-section-header sf-reveal" data-sf-reveal>
          <span className="sf-eyebrow"><span className="sf-pulse" />FAQ</span>
          <h2 className="sf-section-title">
            Answers for decision-makers, <em>not just engineers.</em>
          </h2>
        </div>
        <div className="sf-faq-list">
          {FAQS.map((f, i) => (
            <div key={i} className={`sf-faq-item${openIdx === i ? ' sf-open' : ''}`}>
              <button
                type="button"
                className="sf-faq-summary"
                onClick={() => toggle(i)}
                aria-expanded={openIdx === i}
              >
                {f.q}
                <span className="sf-faq-icon">
                  <svg viewBox="0 0 14 14" fill="none">
                    <path d="M7 3v8M3 7h8" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
                  </svg>
                </span>
              </button>
              <div className="sf-faq-body">
                <div className="sf-faq-body-inner">{f.a}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <style jsx>{`
        .sf-faq {
          background: var(--bg);
          padding: clamp(96px,11vw,150px) 0;
        }
        .sf-faq-list {
          max-width: 760px; margin: 60px auto 0;
          padding: 0 var(--gutter);
        }
        .sf-faq-item {
          margin-bottom: 12px; border-radius: 12px;
          background: #fff; border: 1px solid var(--line);
          overflow: hidden;
          transition: border-color 0.25s,box-shadow 0.25s;
        }
        .sf-faq-item.sf-open {
          border-color: var(--ca-200);
          box-shadow: 0 12px 30px -12px rgba(14,165,233,0.15);
        }
        .sf-faq-summary {
          display: flex; align-items: center; justify-content: space-between;
          padding: 18px 22px; cursor: pointer;
          font-size: 15.5px; font-weight: 500;
          color: var(--text); width: 100%; text-align: left;
          background: none; border: none; font-family: inherit;
        }
        .sf-faq-icon {
          width: 30px; height: 30px;
          display: grid; place-items: center;
          border-radius: 50%; border: 1px solid var(--line);
          background: var(--bg-2); color: var(--text-3);
          transition: transform 0.25s var(--ease),background 0.25s,color 0.25s,border-color 0.25s;
          flex-shrink: 0; margin-left: 16px;
        }
        .sf-faq-icon :global(svg) { width: 14px; height: 14px; }
        .sf-faq-item.sf-open .sf-faq-icon {
          transform: rotate(45deg);
          background: var(--ca-500); color: #fff;
          border-color: var(--ca-500);
        }
        .sf-faq-body {
          max-height: 0; overflow: hidden;
          transition: max-height 0.4s var(--ease);
        }
        .sf-faq-item.sf-open .sf-faq-body { max-height: 600px; }
        .sf-faq-body-inner {
          padding: 0 22px 22px;
          font-size: 14.5px; color: var(--text-2); line-height: 1.7;
        }
      `}</style>
    </section>
  )
}
